import { useEffect, useState } from 'react';

import SocialButton from 'src/components/consumptionPage/ConsumptionMetaData/SocialButtons/SocialButton';
import ToastMessage from 'src/components/toastMessage';
import WatchlistLoginCheckPopup from 'src/components/watchlistLoginCheckPopup/WatchlistLoginCheckPopup';
import {
  addItemToWatchlist,
  fetchWatchlistItems,
  removeItemFromWatchlist
} from 'src/services/apiRequests/WatchlistService';
import { UpdationResponse } from 'src/services/GraphQL/Queries/types';
import { getLocalStorage } from 'src/utils/browserStorageHandler';
import { pausePlayerPopup, resumePlayerPopup } from 'src/utils/common';
import { ContentItemType, DynamicObject } from 'src/utils/commonTypes';
import {
  IconsSetType,
  InitialLoadIconsSet
} from 'src/utils/constants/IconsConstants';
import {
  USER_TOKEN,
  WATCH_LIST_SESSION_STORAGE_OBJECT
} from 'src/utils/constants/localStorageConstants';

import { getSessionStorage } from 'src/components/player/src/utils/Utils';
import { useAppStore } from 'src/hooks/useAppStore';
import { WatchListPayload } from 'src/utils/constants/TvShowDetailsPageConstants';
import {
  MIXPANEL_ADD_WATCHLIST,
  MIXPANEL_REMOVE_WATCHLIST
} from '../../../utils/analytics/mixpanel/mixpanelConstants';
import { HTTP_HEADER_KEYS } from '../../../utils/constants/HttpMethodsConstants';
import { watchlistContainer } from '../MetaButtonsStyle';
import {
  addToWatchlistMixpanelEvent,
  addWatchlistItem,
  checkIfContentAvailableInWatchlist,
  removeFromWatchlistMixpanelEvent,
  removeWatchlistItem
} from './WatchlistHandler';

type WatchlistBtnProps = {
  item: ContentItemType;
  isPresentInWatchlist: boolean;
  updateWatchlistSymbol: () => void;
  contentPartnerDetails?: DynamicObject;
};

const WatchListBtn = (props: WatchlistBtnProps) => {
  const {
    item,
    isPresentInWatchlist,
    updateWatchlistSymbol,
    contentPartnerDetails
  } = props;

  const { userWatchlist, setUserWatchlist, translations } = useAppStore();
  const translationData = translations?.consumption;
  const { WATCHLIST, ADD_WATCHLIST, REMOVE_WATCHLIST } = translationData || {};
  const isLoggedIn = getLocalStorage(USER_TOKEN) ? true : false;
  const headers = [
    HTTP_HEADER_KEYS.AUTHORIZATION,
    HTTP_HEADER_KEYS.X_ACCESS_TOKEN
  ];
  const [toastMsg, setToastMsg] = useState('');
  const [detailsObject, setDetailsObject] = useState<WatchListPayload>();

  useEffect(() => {
    if (toastMsg) {
      setTimeout(() => {
        setToastMsg('');
      }, 2000);
    }
  }, [toastMsg]);

  useEffect(() => {
    addToWatchListPostRedirection();
  }, [isLoggedIn]);

  const [promptUserLoginPopup, setPromptUserLoginPopup] = useState(false);

  const onClose = () => {
    setPromptUserLoginPopup(false);
    resumePlayerPopup();
  };

  const addToWatchListPostRedirection = async () => {
    // Get details data from session storage
    const getDetailsDataFromSessionStorage = getSessionStorage(
      WATCH_LIST_SESSION_STORAGE_OBJECT
    );

    // Check if the details id matches with the id from session storage
    if (item.id !== getDetailsDataFromSessionStorage?.id) {
      return;
    }

    // Check if the content is not already in the watchlist
    if (!checkIfContentAvailableInWatchlist(userWatchlist, item)) {
      const headers = [
        HTTP_HEADER_KEYS.X_ACCESS_TOKEN,
        HTTP_HEADER_KEYS.AUTHORIZATION
      ];

      // Add item to watchlist
      const response = await addItemToWatchlist(
        getDetailsDataFromSessionStorage,
        headers
      );

      // If adding to watchlist was successful
      if (response?.code === 1) {
        setToastMsg(ADD_WATCHLIST);
      }

      // Fetch updated watchlist items
      const watchlistResponse = await fetchWatchlistItems(headers);

      // Update user watchlist
      setUserWatchlist({ ...watchlistResponse });

      // Track event in Mixpanel
      addToWatchlistMixpanelEvent(item as ContentItemType);
    }
  };

  const addToWatchlistHandler = async () => {
    const requestObj = {
      id: item.id,
      assetType: item.assetType,
      duration: item.duration,
      date: item.releaseDate || new Date().toISOString()
    };
    if (isLoggedIn) {
      const response: UpdationResponse = await addItemToWatchlist(
        requestObj,
        headers
      );
      if (Number(response?.code) === 1) {
        setToastMsg(ADD_WATCHLIST);
      }
      updateWatchlistSymbol();
      const updatedWatchlistData = addWatchlistItem(userWatchlist, item);
      setUserWatchlist(updatedWatchlistData);
      addToWatchlistMixpanelEvent(item, contentPartnerDetails);
    } else {
      setDetailsObject(requestObj);
      setPromptUserLoginPopup(true);
      pausePlayerPopup();
    }
  };

  const removeFromWatchlistHandler = async () => {
    const requestObj = {
      id: item.id,
      assetType: item.assetType
    };
    const response = await removeItemFromWatchlist(requestObj, headers);
    if (Number(response?.code) === 1) {
      setToastMsg(REMOVE_WATCHLIST);
    }
    updateWatchlistSymbol();
    const updatedWatchlistData = removeWatchlistItem(userWatchlist, item);
    setUserWatchlist(updatedWatchlistData);
    removeFromWatchlistMixpanelEvent(item, contentPartnerDetails);
  };

  return (
    <div>
      <div
        css={watchlistContainer}
        title={WATCHLIST}
        data-testid="watchlist-btn-container">
        <SocialButton
          icon={
            isPresentInWatchlist
              ? InitialLoadIconsSet.REMOVE_FROM_WATCHLIST
              : InitialLoadIconsSet.ADD_TO_WATCHLIST
          }
          type={IconsSetType.INITIAL_LOAD}
          name={WATCHLIST}
          action={
            isPresentInWatchlist
              ? removeFromWatchlistHandler
              : addToWatchlistHandler
          }
          id={
            isPresentInWatchlist
              ? 'remove-from-watchlist-btn'
              : 'added-to-watchlist-btn'
          }
        />
      </div>
      <WatchlistLoginCheckPopup
        show={promptUserLoginPopup}
        onClose={onClose}
        detailsObject={detailsObject}
      />
      {toastMsg ? (
        <ToastMessage
          message={toastMsg}
          mixpanelMessage={
            isPresentInWatchlist
              ? MIXPANEL_ADD_WATCHLIST
              : MIXPANEL_REMOVE_WATCHLIST
          }
          contentPartnerDetails={contentPartnerDetails}
        />
      ) : null}
    </div>
  );
};

export default WatchListBtn;
